import React from 'react';
import { TrendingUp, TrendingDown, AlertTriangle, CheckCircle2, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';

interface BudgetInsightsProps {
  income: number;
  expenses: {
    needs: number;
    wants: number;
    savings: number;
  };
  taxRate: number;
}

export const BudgetInsights: React.FC<BudgetInsightsProps> = ({
  income,
  expenses,
  taxRate
}) => {
  const totalExpenses = expenses.needs + expenses.wants + expenses.savings;
  const monthlyIncome = income;
  const remainingBudget = monthlyIncome - totalExpenses;
  
  const needsPercentage = (expenses.needs / monthlyIncome) * 100;
  const wantsPercentage = (expenses.wants / monthlyIncome) * 100;
  const savingsPercentage = (expenses.savings / monthlyIncome) * 100;

  const insights = [
    {
      title: "Needs Ratio",
      target: "50%",
      current: needsPercentage,
      status: needsPercentage <= 50 ? "good" : "warning",
      message: needsPercentage <= 50 
        ? "Your essential expenses are within the recommended range."
        : "Consider reducing essential expenses to meet the 50% target."
    },
    {
      title: "Wants Ratio",
      target: "30%",
      current: wantsPercentage,
      status: wantsPercentage <= 30 ? "good" : "warning",
      message: wantsPercentage <= 30
        ? "Your discretionary spending is well-managed."
        : "Consider cutting back on non-essential expenses."
    },
    {
      title: "Savings Ratio",
      target: "20%",
      current: savingsPercentage,
      status: savingsPercentage >= 20 ? "good" : "warning",
      message: savingsPercentage >= 20
        ? "You're meeting or exceeding the recommended savings rate."
        : "Try to increase your savings to reach the 20% target."
    }
  ];

  const recommendations = [
    remainingBudget < 0 && "Your expenses exceed your income. Consider reducing expenses or finding additional income sources.",
    needsPercentage > 50 && "Your essential expenses are high. Look for ways to reduce housing, transportation, or utility costs.",
    wantsPercentage > 30 && "Your discretionary spending could be reduced. Track your non-essential purchases more carefully.",
    savingsPercentage < 20 && "Increase your savings rate by automating transfers to savings accounts.",
    taxRate > 25 && "Consider tax-advantaged accounts or deductions to reduce your tax burden."
  ].filter(Boolean);

  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-3 gap-4">
        {insights.map((insight, index) => (
          <motion.div
            key={insight.title}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: index * 0.1 }}
            className="glass-card p-6 border border-emerald-500/20"
          >
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold">{insight.title}</h3>
              {insight.status === "good" ? (
                <CheckCircle2 className="w-5 h-5 text-emerald-500" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-amber-500" />
              )}
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-slate-400">
                <span>Current</span>
                <span>Target</span>
              </div>
              <div className="flex justify-between">
                <span className="text-2xl font-bold">{insight.current.toFixed(1)}%</span>
                <span className="text-2xl font-bold text-emerald-500">{insight.target}</span>
              </div>
              <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`h-full ${insight.status === "good" ? "bg-emerald-500" : "bg-amber-500"}`}
                  style={{ width: `${Math.min(100, insight.current)}%` }}
                />
              </div>
              <p className="text-sm text-slate-400">{insight.message}</p>
            </div>
          </motion.div>
        ))}
      </div>

      {recommendations.length > 0 && (
        <div className="glass-card p-6 border border-emerald-500/20">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-emerald-500" />
            Recommendations
          </h3>
          <ul className="space-y-2">
            {recommendations.map((recommendation, index) => (
              <motion.li
                key={index}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="flex items-start gap-2 text-slate-300"
              >
                <ArrowRight className="w-4 h-4 text-emerald-500 mt-1 flex-shrink-0" />
                {recommendation}
              </motion.li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};